import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, X, Quote } from "lucide-react";
import { LINKS, ASSETS } from "../lib/constants";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

const SectionLabel = ({ children, dark = false, onYellow = false }) => (
  <span className={`section-label ${dark ? "on-dark" : ""} ${onYellow ? "on-yellow" : ""}`}>{children}</span>
);

const Area = ({ num, title, body, pill }) => (
  <div className="bg-white border-[1.5px] border-brand-black rounded-[10px] p-7 transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0_#0A0A0A]">
    <p className="text-xs uppercase tracking-[0.18em] text-brand-stone font-bold">Area {num}</p>
    <h3 className="font-display text-2xl mt-3 mb-3 uppercase">{title}</h3>
    <p className="text-[15px] leading-relaxed text-brand-ink">{body}</p>
    <p className="mt-4 text-[11px] uppercase tracking-[0.18em] font-bold text-brand-black/70">{pill}</p>
  </div>
);

const Include = ({ num, title, body, bonus }) => (
  <div className="p-6 rounded-[10px] border-[1.5px] border-brand-yellow bg-white/[0.04] transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_#F5C632]">
    <span className="text-xs uppercase tracking-[0.18em] font-bold text-brand-yellow">{num}</span>
    <h4 className="font-display text-xl mt-3 mb-2 leading-tight uppercase text-white">{title}</h4>
    <p className="text-[14px] leading-relaxed text-white/85">{body}</p>
    {bonus && <p className="mt-3 text-[13px] leading-relaxed text-brand-yellow"><strong>Bonus:</strong> {bonus}</p>}
  </div>
);

export default function Advisory() {
  const [form, setForm] = useState({ name: "", email: "", business: "", revenue: "", team: "", heavy: "", container: "", website: "" });
  const [status, setStatus] = useState("idle");
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!WEB3FORMS_KEY) {
      window.location.href = `mailto:${LINKS.email}?subject=Advisory application from ${encodeURIComponent(form.name)}`;
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `Advisory application — ${form.name}`,
          from_name: form.name,
          ...form,
        }),
      });
      const data = await res.json();
      if (data.success) setStatus("success");
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  return (
    <div data-testid="advisory-page">
      {/* HERO */}
      <section className="bg-white pt-12 pb-16 lg:pt-16 lg:pb-20">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-center reveal">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative aspect-[4/5] max-w-[460px] mx-auto lg:mx-0">
              <div className="absolute inset-0 translate-x-3 translate-y-3 bg-brand-yellow rounded-[10px]" />
              <div className="absolute inset-0 -translate-x-2 -translate-y-2 rounded-[10px] overflow-hidden border-[3px] border-brand-black">
                <img src={ASSETS.advisoryHero} alt="Magdalena Mahdy — Profit & Cashflow Strategist" className="w-full h-full object-cover" data-testid="advisory-hero-portrait" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <SectionLabel>Profit &amp; Cashflow Advisory Programme</SectionLabel>
            <h1 className="font-display text-[clamp(2.4rem,5.6vw,4.8rem)] mt-6 leading-[0.92]">
              Ongoing support for the <span className="hi-yellow">bigger money decisions.</span>
            </h1>
            <p className="mt-7 text-lg lg:text-xl leading-relaxed text-brand-ink max-w-3xl">
              Private profit and cashflow support — <strong>Part 4 of Follow The Cash™</strong> — for
              purpose-led business owners who are ready to own their future, stop carrying every
              financial decision alone, and leave <strong>Profitable But Broke</strong> behind for
              good.
            </p>
            <p className="mt-5 text-[17px] leading-relaxed text-brand-ink max-w-3xl">
              There comes a point where figuring it out as you go becomes too expensive. The business
              is no longer asking for more effort. It is asking for more structure, clearer
              visibility and someone steady beside you as the decisions get bigger.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#advisory-apply" className="btn-yellow" data-testid="advisory-hero-apply-btn">
                Apply to the programme <ArrowUpRight size={18} />
              </a>
              <a href="#advisory-programme" className="btn-secondary" data-testid="advisory-hero-inside-btn">
                See what's inside
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SHIFT */}
      <section className="bg-brand-yellow py-20 lg:py-24 border-y-[1.5px] border-brand-black">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel onYellow>The shift you've been quietly waiting for</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
            You have outgrown <span className="hi-white">holding it all in your head.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-brand-black">
            Your business is no longer tiny. The numbers are bigger. The decisions are heavier. The
            team — or the support you are about to bring in — depends on you. And privately, you
            have started noticing the cost of leading without enough financial structure underneath
            you.
          </p>
          <p className="mt-6 text-[17px] uppercase tracking-[0.05em] font-bold text-brand-black">You might be asking:</p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Is this growth path actually sustainable for me — or am I just outrunning it?",
              "What does my next hire really cost — and when is it safe to make it?",
              "Which offer should we double down on, and which is quietly draining margin?",
              "Am I paying myself in a way that reflects the role I'm actually carrying?",
              "What would my next 12 months look like if I planned it properly, instead of reacting?",
              "Who is holding the bigger money picture with me?",
            ].map((q, i) => (
              <div key={i} className="bg-white border-[1.5px] border-brand-black rounded-[10px] p-5 flex items-start gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_#0A0A0A]">
                <span className="font-display text-4xl text-brand-black leading-none shrink-0">?</span>
                <p className="text-[15px] leading-relaxed text-brand-ink self-center">{q}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-lg leading-relaxed text-brand-black">
            These aren't beginner questions. They're the questions that come with real momentum.
            They deserve more than another tab open in your brain at 11pm. They deserve a calm and
            steady second brain, and a practical approach that feels safe and makes sense for
            finding the answers.
          </p>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel>Who this is for</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
            For the founder who is done winging the <span className="hi-yellow">money side of growth.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-brand-ink">
            This is a private, high-touch advisory partnership. It is intentionally limited because
            the work is personal, practical and built around the real business in front of us.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="bg-white border-[1.5px] border-brand-black rounded-[10px] p-7">
              <h4 className="font-display text-xl uppercase mb-4">This is for you if…</h4>
              <ul className="space-y-2 text-[15px] text-brand-ink">
                {[
                  "You're a purpose-led founder running an established business that has real momentum.",
                  "You are scaling, restructuring, hiring, simplifying or stepping into a bigger version of the business.",
                  "You are tired of carrying every money decision in your own head.",
                  "You want profit clarity, cashflow forecasts, scenarios and practical decision support.",
                  "You value calm, considered, customised support over noise, hype or template-driven coaching.",
                  "You are ready to look honestly at what the business needs next.",
                ].map((x) => <li key={x} className="flex gap-2.5"><Check size={17} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />{x}</li>)}
              </ul>
            </div>
            <div className="bg-brand-black text-white border-[1.5px] border-brand-black rounded-[10px] p-7">
              <h4 className="font-display text-xl uppercase mb-4">This isn't for you if…</h4>
              <ul className="space-y-2 text-[15px] text-white/90">
                {[
                  "You're looking for bookkeeping, compliance accounting or someone to file your tax return.",
                  <>You want a one-off fix or a quick clarity session — the <Link to={LINKS.moneyFlowSession} className="underline decoration-brand-yellow decoration-2 underline-offset-2">Money Flow Strategy Session</Link> is a better starting point.</>,
                  "You're not yet ready to look honestly at how the business is currently being led, financially and strategically.",
                  "You're chasing more revenue without addressing structure, profit, capacity or pay.",
                  "You want a full, in-house finance department — this is advisory partnership, not headcount.",
                ].map((x, i) => <li key={i} className="flex gap-2.5"><X size={17} className="text-brand-yellow shrink-0 mt-0.5" /><span>{x}</span></li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FOLLOW THE CASH METHOD */}
      <section id="advisory-programme" className="bg-brand-yellow py-20 lg:py-24 border-y-[1.5px] border-brand-black">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel onYellow>The Follow The Cash™ Method</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
            Four parts. One calmer path <span className="hi-white">through the numbers.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-brand-black">
            Inside the advisory partnership, we work through the full Follow The Cash™ method at
            your pace, mapped to your business, your numbers and the chapter you are actually in.
            Not a course. Not a generic curriculum. A living piece of work held between us.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Area num="01" title="Stop Hiding" pill="Visibility · Diagnosis · Honesty" body="We surface the financial truth of the business — revenue rhythm, offer profitability, expenses, personal pay and capacity — without shame or overwhelm. You finally see what's working, what's leaking and where to focus first." />
            <Area num="02" title="Start Knowing" pill="Profit · Cash · Understanding" body="We decode what the numbers actually mean. Profit vs. cash. Margins. Pricing. The gap between what the P&L says and what the bank account shows. You stop guessing and start reading the story your numbers are telling." />
            <Area num="03" title="Take Control" pill="Pay · Forecasting · Steadiness" body="We build a calmer cashflow rhythm: personal pay structure, 6–12 month forecasting, tax planning, buffer thinking and a clearer view of what's coming. Decisions become evidence-based, not anxiety-based." />
            <Area num="04" title="Own Your Future" pill="Vision · Rhythm · Sustainability" body="We connect the money to the bigger vision: time design, sustainable growth, future offers, personal pay and a business that supports your life. Growth without running dry. Resilience against the inevitable bumps." />
          </div>

          <div className="mt-14 max-w-3xl mx-auto rounded-[10px] border-[1.5px] border-brand-black bg-brand-black text-white p-7 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_#FFFFFF]">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-yellow font-bold">The frame</p>
            <h4 className="font-display text-2xl mt-2 mb-3 uppercase">Not a course. A relationship.</h4>
            <p className="text-[15px] leading-relaxed text-white/90">
              Every part feeds the next. Stopping the hiding makes the knowing possible. Knowing the
              numbers makes taking control natural. Taking control makes owning your future real.
              That's the Follow The Cash™ method.
            </p>
          </div>
        </div>
      </section>

      {/* INSIDE THE PARTNERSHIP */}
      <section className="bg-brand-black text-white py-20 lg:py-24">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel dark>Inside the advisory partnership</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95] text-white">
            Everything held in your <span className="text-brand-yellow">private programme.</span>
          </h2>
          <p className="mt-6 text-[17px] leading-relaxed text-white/85">
            Each partnership is shaped around your business, your stage and the support you
            actually need. The list below is the foundation — not the ceiling. The depth and rhythm
            flexes with what's true for you.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Include num="01" title="Deep onboarding intensive" body="A private 2-hour deep dive to map the business, the numbers, the pressure points and the first 90-day priorities." />
            <Include num="02" title="Monthly strategy sessions" body="One to two private 60–90 minute calls each month for decisions, forecasts, scenarios and blind spots." />
            <Include num="03" title="Profit and cashflow views" body="Rolling 6–12 month forecasts, profitability and personal-pay planning that evolve with your business." bonus="Free access to forecastly, my own cashflow forecasting software — yours for the duration of the partnership." />
            <Include num="04" title="Decision support" body="Hiring, pricing, investment and simplify-or-grow decisions held with more evidence." />
            <Include num="05" title="Between-session support" body="Within agreed hours, access for check-ins and 'should I or shouldn't I?' moments." />
            <Include num="06" title="Quarterly reviews" body="Step-back sessions to review what is working, what is changing and what the next quarter needs." />
            <Include num="07" title="The Follow The Cash™ Method" body="All four parts are woven through this partnership, customised to your needs." />
            <Include num="08" title="Held with care" body="This is high-touch, customised work. You're seen, heard and held — not pushed, not templated, not rushed." />
            <Include num="09" title="Calm exit, not a hard ending" body="Every partnership closes with a strategic outlook for the next chapter." />
          </div>
        </div>
      </section>

      {/* THREE LEVELS */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <SectionLabel>Three levels of partnership</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
            Choose the depth of support<br />that <span className="hi-yellow">matches your chapter.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-brand-ink max-w-3xl mx-auto">
            You do not need to choose the length before applying. The Advisory Fit Call is there to
            assess where you are and what would actually serve you.
          </p>

          <div className="mt-12 grid md:grid-cols-3 gap-6 text-left">
            {[
              {
                length: "3 Months",
                title: "Profit & Cashflow Reset",
                turnover: "£80k – £180k",
                blurb: "A focused recalibration for founders who need cleaner priorities, sharper visibility and a stronger base before making the next move.",
                bullets: ["Strategic onboarding review", "Monthly advisory calls", "Profit and offer review", "Personal pay and cashflow check", "90-day priorities", "Async decision support"],
                best: "Best for · Recalibration",
                highlight: false,
              },
              {
                length: "6 Months",
                title: "Profit & Cashflow Advisory",
                turnover: "£180k – £350k",
                blurb: "For founders actively growing, refining, hiring, simplifying or restructuring. Enough time to land real change and see decisions today show up in the numbers later.",
                bullets: ["Everything in the 3-month partnership", "Full 4-part Follow The Cash™ Method", "Offer profitability and pricing review", "Hiring and investment decision support", "Quarterly planning", "Personal pay restructuring"],
                best: "Best for · Sustained transformation",
                highlight: true,
              },
              {
                length: "12 Months",
                title: "Private Profit & Cashflow Partnership",
                turnover: "£350k+",
                blurb: "A full year of strategic support for founders entering a more established stage: building a team, refining the model or stepping into a bigger version of the business.",
                bullets: ["Everything in the 6-month partnership", "Rolling cashflow view across the year", "Quarterly strategic planning", "Investment and hiring decision support", "Long-term life and business design", "Annual profit and cashflow review"],
                best: "Best for · Strategic transition",
                highlight: false,
              },
            ].map((p, i) => (
              <div key={i} className={`rounded-[10px] p-7 flex flex-col border-[1.5px] ${p.highlight ? "border-brand-black bg-brand-yellow" : "border-brand-black bg-white"} transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0_#0A0A0A]`}>
                {p.highlight && <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold bg-brand-black text-brand-yellow px-2 py-1 rounded-sm mb-3 self-start">Most chosen</span>}
                <p className="text-xs uppercase tracking-[0.18em] font-bold text-brand-black/80">{p.length}</p>
                <h3 className="font-display text-2xl mt-2 mb-2 uppercase leading-tight">{p.title}</h3>
                <p className="text-[13px] text-brand-black/80">For businesses <strong>{p.turnover}</strong> turnover</p>
                <p className="mt-4 text-[15px] leading-relaxed text-brand-ink flex-grow">{p.blurb}</p>
                <ul className="mt-5 space-y-1.5 text-[13px] text-brand-ink">
                  {p.bullets.map((b) => <li key={b} className="flex gap-2"><Check size={14} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />{b}</li>)}
                </ul>
                <p className="mt-5 text-[11px] uppercase tracking-[0.18em] font-bold text-brand-black/70">{p.best}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-[17px] leading-relaxed text-brand-ink max-w-3xl mx-auto">
            <strong>From £800/month.</strong> This is a bespoke advisory partnership, so final
            pricing is confirmed after your Advisory Fit Call. It depends on turnover, business
            model, team size, cashflow complexity and the depth of support required.
          </p>

          <div className="mt-9 flex justify-center">
            <a href="#advisory-apply" className="btn-primary shadow-w-default" data-testid="three-levels-apply-btn">
              Apply to the Programme <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="bg-brand-yellow py-20 lg:py-24 border-y-[1.5px] border-brand-black">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel onYellow>What happens next</SectionLabel>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] mt-6 leading-[0.95]">
            From private chat to <span className="hi-white">steady partnership.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-brand-black">
            This isn't a "click to buy" offer. It's a private programme, joined by application —
            because the relationship matters as much as the framework. Here's exactly how the
            journey begins.
          </p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { s: "01", t: "Apply", b: "You submit a short, private application so I can prepare properly." },
              { s: "02", t: "Advisory Fit Call", b: "We have a 20–30 minute conversation about where the business is, what feels heavy and which level of support would actually serve you." },
              { s: "03", t: "Onboarding intensive", b: "If we both decide it is the right fit, we begin with a 2-hour deep dive into the business, the numbers and the first 90-day priorities." },
              { s: "04", t: "The partnership", b: "Monthly strategy sessions, forecasts, decision support and the Follow The Cash™ method woven through your real business." },
              { s: "05", t: "Strategic outlook", b: "Every partnership closes with a clear view of the next chapter and an honest conversation about whether continued support is right." },
            ].map((x) => (
              <div key={x.s} className="bg-white border-[1.5px] border-brand-black rounded-[10px] p-5">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand-stone">Step {x.s}</p>
                <h4 className="font-display text-xl mt-2 mb-2 uppercase leading-tight">{x.t}</h4>
                <p className="text-[13px] leading-relaxed text-brand-ink">{x.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOME */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel>The outcome</SectionLabel>
            <h2 className="font-display text-[clamp(2.2rem,5vw,3.6rem)] mt-6 leading-[0.95]">
              What changes when you stop <span className="hi-yellow">carrying it alone.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-lg leading-relaxed text-brand-ink">
            <p>
              The shift inside this work is rarely loud. It's not a viral revenue moment or a
              dramatic before-and-after. It's quieter than that — and far more important.{" "}
              <strong>You start leading from evidence, not anxiety.</strong> You start sleeping
              again. You stop checking the bank balance for reassurance. You start making decisions
              from forecast, framework and self-trust. You become the CEO your business has
              actually needed all along.
            </p>
            <p className="font-display text-2xl uppercase leading-[1.3]">
              You stop being Profitable But Broke. You start <span className="hi-yellow">owning your future.</span> That's what Part 4 of Follow The Cash™ delivers.
            </p>
            <ul className="mt-4 space-y-2 text-[15px]">
              {[
                "You understand the money picture without having to decode it alone",
                "You know which offers, costs and decisions need attention",
                "You pay yourself with more structure and less leftovers energy",
                "You stop using the bank balance as your main business strategy",
                "You make hiring, pricing and investment decisions with more evidence",
                "You have a clearer 6–12 month view of what is coming",
                "You feel less alone inside the leadership of the business",
              ].map((x) => <li key={x} className="flex gap-2.5"><Check size={17} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />{x}</li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* NOTE FROM MAGDALENA */}
      <section className="bg-brand-black text-white py-20 lg:py-24">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <div className="relative aspect-[4/5] max-w-[380px] mx-auto rounded-[10px] overflow-hidden border-[3px] border-brand-yellow">
              <img src={ASSETS.portraitYellowBg} alt="Magdalena, founder of ProfitMIND" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <SectionLabel dark>A note from Magdalena</SectionLabel>
            <Quote size={40} className="text-brand-yellow mt-4" strokeWidth={1.5} />
            <p className="font-display text-3xl sm:text-4xl leading-tight mt-3 mb-6">
              "You shouldn't have to lead the next era of your business alone."
            </p>
            <div className="space-y-5 text-[17px] leading-relaxed max-w-3xl">
              <p>
                You have built something real. The complexity is not a sign that you are failing.
                It is a sign that the business has grown. What you need now is not more pressure —
                it is a private space to think clearly with someone who understands both the
                numbers and the founder carrying them.
              </p>
              <p>
                I know what it means to carry the financial weight of a seasonal business alone.
                <strong> Profitable But Broke</strong> was my reality before I had a method for it.
                This partnership is how I help you leave it behind — permanently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="advisory-apply" className="bg-brand-yellow py-20 lg:py-24 border-y-[1.5px] border-brand-black">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-5">
              <SectionLabel onYellow>Apply to the programme</SectionLabel>
              <h2 className="font-display text-[clamp(2.2rem,4.6vw,3.6rem)] mt-6 leading-[0.95]">
                A short, private application so the call starts on <span className="hi-white">the right page.</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-brand-black">
                Because this is high-touch advisory work for a limited number of founders at a time,
                the first step is a short application. Five quick questions help me understand your
                business, what feels heavy, and whether this kind of partnership is likely to serve
                you.
              </p>
              <ul className="mt-7 space-y-2 text-sm text-brand-black">
                <li className="flex gap-2"><Check size={16} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />Takes around two minutes.</li>
                <li className="flex gap-2"><Check size={16} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />Read personally by Magdalena.</li>
                <li className="flex gap-2"><Check size={16} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />Completely confidential.</li>
                <li className="flex gap-2"><Check size={16} className="text-brand-black shrink-0 mt-0.5" strokeWidth={3} />No long forms. No fluff.</li>
              </ul>
              <p className="mt-8 text-sm uppercase tracking-[0.18em] font-bold text-brand-black">Prefer a chat first?</p>
              <a href={LINKS.tidycal} target="_blank" rel="noopener noreferrer" className="btn-secondary mt-3" data-testid="advisory-apply-chat-btn">
                Book a free 20-min chat <ArrowUpRight size={16} />
              </a>
            </div>

            <div className="lg:col-span-7">
              {status === "success" ? (
                <div className="bg-brand-black text-white rounded-[10px] p-8 text-center" data-testid="advisory-form-success">
                  <h3 className="font-display text-3xl mb-3">Thank you — your application is in.</h3>
                  <p className="text-white/85 mb-5">
                    I read every application personally, usually within 1–2 working days. As a next
                    step, you can go ahead and book your <strong>Advisory Fit Call</strong> using
                    the private link below — I'll come into our conversation already having read
                    what you shared.
                  </p>
                  <a href={LINKS.advisoryFitCall} target="_blank" rel="noopener noreferrer" className="btn-yellow btn-yellow-on-dark" data-testid="advisory-fit-call-btn">
                    Book your Advisory Fit Call <ArrowUpRight size={16} />
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 bg-white border-[1.5px] border-brand-black rounded-[10px] p-7 sm:p-9" data-testid="advisory-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Your name *</label>
                      <input required name="name" value={form.name} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="advisory-name" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Email *</label>
                      <input required type="email" name="email" value={form.email} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="advisory-email" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Business name or website</label>
                    <input name="business" value={form.business} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="advisory-business" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Annual revenue range *</label>
                      <select required name="revenue" value={form.revenue} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md text-sm bg-white focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="advisory-revenue">
                        <option value="">Select a range…</option>
                        <option>Under £100k</option>
                        <option>£100k – £250k</option>
                        <option>£250k – £500k</option>
                        <option>£500k – £1m</option>
                        <option>Over £1m</option>
                        <option>Prefer not to say</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Team size *</label>
                      <select required name="team" value={form.team} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md text-sm bg-white focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="advisory-team">
                        <option value="">Select team size…</option>
                        <option>Just me</option>
                        <option>1 – 3 people (incl. contractors)</option>
                        <option>4 – 10 people</option>
                        <option>10+ people</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">What's the heaviest part of the money side right now? *</label>
                    <textarea required rows={4} name="heavy" value={form.heavy} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow resize-none" data-testid="advisory-heavy" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Which container feels right (best guess is fine)?</label>
                    <select name="container" value={form.container} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md text-sm bg-white focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="advisory-container">
                      <option value="">Select…</option>
                      <option>Not sure yet — let's figure it out on the call</option>
                      <option>3 months</option>
                      <option>6 months</option>
                      <option>12 months</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Website</label>
                    <input name="website" value={form.website} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="advisory-website" />
                  </div>
                  <button type="submit" disabled={status === "sending"} className="btn-primary shadow-w-default disabled:opacity-60" data-testid="advisory-submit">
                    {status === "sending" ? "Sending…" : (<>Send application <ArrowUpRight size={16} /></>)}
                  </button>
                  {status === "error" && <p className="text-xs text-red-600">Something went wrong — please email {LINKS.email} directly.</p>}
                  <p className="text-[11px] text-brand-stone leading-relaxed">
                    By submitting, you confirm you've read our <a href="/privacy-policy" className="underline decoration-brand-black underline-offset-2">Privacy Policy</a>. Your information is only used by Magdalena to respond to your application.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-black py-20 lg:py-28" data-testid="advisory-final-cta">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-brand-yellow border-[1.5px] border-brand-yellow px-4 py-2 rounded-full">
            From £800 / month · By application
          </span>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4.4rem)] leading-[0.95] mt-8 text-white">
            You don't have to lead the next chapter of your business <span className="text-brand-yellow">alone.</span>
          </h2>
          <p className="mt-7 text-lg leading-relaxed text-white/85 mx-auto max-w-2xl">
            You've carried the strategy, the numbers, the team, the offers, the forecast and the
            long-term direction in your own head for long enough. The partnership you've been
            quietly looking for exists — and it's private, practical, and built around the real
            business in front of us.
          </p>
          <p className="mt-6 font-display text-xl sm:text-2xl uppercase leading-[1.35] text-brand-yellow mx-auto max-w-2xl">
            A trusted second brain. A clearer path. A calmer next chapter.
          </p>

          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <a href="#advisory-apply" className="btn-yellow btn-yellow-on-dark" data-testid="advisory-final-apply-btn">
              Apply to the programme <ArrowUpRight size={18} />
            </a>
            <Link to={LINKS.moneyFlowSession} className="inline-flex items-center gap-2 px-6 py-3 border-[1.5px] border-white text-white text-sm font-bold uppercase tracking-wide rounded-md hover:bg-white hover:text-brand-black transition-colors" data-testid="advisory-final-explore-btn">
              Or explore the Money Flow Session
            </Link>
          </div>

          <p className="mt-10 text-sm uppercase tracking-[0.18em] font-bold text-brand-yellow">
            Part 4 of Follow The Cash™. Own Your Future.
          </p>
        </div>
      </section>
    </div>
  );
}
